import { Button, Loader, Sidebar, TextArea } from 'components/common';
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { t } from 'i18next';
import Timeline from './LeaveTimelineSidebar';
import SameDepartmentRequests from './SameDepartmentRequests';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import { toast } from 'react-toastify';
import {
  acceptedConge,
  cancelConge,
  deleteConge,
  forceAcceptConge,
  GetDetailsConge,
  rejectConge,
} from '../services/leaveService';
import { checkFileType, getIconByExtension } from 'utils/globalMethodes';
import { InfoRow, StatusRow } from './LeaveRequestComponents';
import { hasPermission } from 'utils/permissions';
import useClickOutside from 'hooks/useClickOutside';
import { useDialog } from 'contexts/DialogContext';
import {
  Approval,
  CreateBy,
  CreateCongeType,
  Decision,
  DemandeUser,
  MainResponse,
  UserDep,
} from '../types/leaveRequestType';
import CreateConge from './CreateConge';
import { useAuthContext } from 'contexts/AuthContext';
import { Form, Formik } from 'formik';
import { useMediaContext } from 'contexts/MediaContext';
import moment from 'moment';
import * as Yup from 'yup';
import UserShortCut from 'components/common/UserShortCut';
import LucideIcon from 'components/common/LucideIcon';
import Tippy from '@tippyjs/react';

interface LeaveDetailsParams {
  id: number;
  deleteEvent?: () => void;
  refetch?: () => void;
}

const LeaveDetails: React.FC<SideBarProps<LeaveDetailsParams>> = ({
  sidebar,
  setSidebarWidth,
  sidebarWidth,
  params,
}) => {
  const { openMedia } = useMediaContext();
  const { closeSidebar, openSidebar } = useSidebarManager();
  const [refetch, setRefetch] = useState(false);
  const [demandeUser, setDemandeUser] = useState<DemandeUser>();
  const [departmentRequest, setDepartmentRequest] = useState<UserDep[]>();
  const [mainResponse, setMainResponse] = useState<MainResponse>();
  const [createBy, setCreateBy] = useState<CreateBy>();
  const [approval, setApproval] = useState<Approval[]>([]);
  const [accepting, setAccepting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const dropdownRef = useRef(null);
  const [showRejectReason, setShowRejectReason] = useState(false);
  const dialog = useDialog();
  const { user } = useAuthContext();

  useClickOutside(dropdownRef, setShowRejectReason, showRejectReason);
  const fetchData = async (): Promise<void> => {
    try {
      const response = await GetDetailsConge(params.id);
      if (response.success) {
        setMainResponse(response.data);
        setCreateBy(response.data.createBy);
        setApproval(response.data.approval);
        setDepartmentRequest(response.data.demanedInSameDepartment);
        setDemandeUser(response.data.demandeUser);
      } else {
        closeSidebar(LeaveDetails);
        toast.error(response.message);
      }
    } catch (error) {
      closeSidebar(LeaveDetails);
      console.error('Error fetching leave details:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [params, refetch]);

  const handleEdit = (): void => {
    if (!mainResponse) return;

    const initValues: CreateCongeType = {
      id: mainResponse.id,
      fk_User: mainResponse.fk_User,
      fk_RegimeType: String(mainResponse.fk_RegimeType),
      projectId: mainResponse.projectId ? String(mainResponse.projectId) : null,
      startDate: String(mainResponse.startDate),
      endDate: String(mainResponse.endDate),
      afternoon: mainResponse.afternoon,
      morning: mainResponse.morning,
      description: mainResponse.description,
      files: [],
      filesNames: mainResponse.filesNames ?? [],
      startTime: mainResponse.startTime,
      endTime: mainResponse.endTime,
      fk_Substitute: mainResponse.fk_Substitute,
    };
    openSidebar(CreateConge, true, { initValues, refetch: params.refetch });
  };

  const handleDelete = async (event: React.FormEvent): Promise<void> => {
    dialog
      .delete('conge.Delete_title', 'conge.Delete_description')
      .then(async (res) => {
        if (res) {
          event.preventDefault();
          if (!mainResponse) return;

          const response = await deleteConge(mainResponse.id);
          if (response.success) {
            toast.success(t('Default.succesMessage'));
            if (params?.deleteEvent) {
              params.deleteEvent();
            }
            if (params?.refetch) {
              params.refetch();
            }
          } else {
            toast.error(response.message);
          }

          closeSidebar(LeaveDetails);
        }
      });
  };

  const handleCancel = (): void => {
    const handelSubmitCancel = (values: { raison: string }): void => {
      handleSubmitCancel(values.raison);
    };

    dialog.custom(
      <Formik initialValues={{ raison: '' }} onSubmit={handelSubmitCancel}>
        {({ isSubmitting }) => (
          <Form
            className="minthr-box-shadow minthr-border-rounded  minthr-bg-white minthr-p-3 show "
            style={{
              width: 'calc(var(--unit-minthr)*400)',
            }}
          >
            <div className="minthr-mb-4 minthr-flex minthr-flex-items-center w-100">
              <LucideIcon
                name="TriangleAlert"
                size="l"
                color="#F5A623"
                className="minthr-mr-2"
              />
              <h4>{t('Leave.Cancel.Reason', "Reason de l'annulation")}</h4>
              <button
                className="minthr-sidebar-close minthr-ml-auto"
                type="button"
                onClick={() => {
                  dialog.close();
                }}
              >
                <img
                  className="minthr-icon minthr-icon-gray"
                  alt="Close"
                  src="/icons/close.svg"
                />
              </button>
            </div>
            <TextArea label={t('Raison')} name="raison" />

            <Button
              type="submit"
              className="minthr-btn minthr-btn-warning minthr-ml-auto minthr-mt-4"
              disabled={isSubmitting}
            >
              {t('btn.cancel')}
            </Button>
          </Form>
        )}
      </Formik>,
    );
  };

  const handleSubmitCancel = async (raison: string): Promise<void> => {
    if (!mainResponse) return;
    setCancelling(true);

    const payload: Decision = {
      id: mainResponse.id,
      levelType: mainResponse.levelType,
      raison: raison.trim(),
    };
    const response = await cancelConge(payload);
    if (response.success) {
      setCancelling(false);
      dialog.close();
      closeSidebar(LeaveDetails);
      toast.success(t('Default.succesMessage'));
      if (params?.refetch) {
        params.refetch();
      }
      setRefetch((prev) => !prev);
    } else {
      setCancelling(false);
      toast.error(response.message);
    }
  };

  const handleReject = (): void => {
    const handelSubmitReject = (values: { raison: '' }): void => {
      handleSubmitReject(values.raison);
    };

    dialog.custom(
      <Formik
        initialValues={{ raison: '' }}
        onSubmit={handelSubmitReject}
        validationSchema={Yup.object().shape({
          raison: Yup.string().trim().required(t('RequiredAttribute')),
        })}
      >
        {({ isSubmitting }) => (
          <Form
            className="minthr-box-shadow minthr-border-rounded  minthr-bg-white minthr-p-3 show "
            style={{
              width: 'calc(var(--unit-minthr)*400)',
            }}
          >
            <div className="minthr-mb-4 minthr-flex minthr-flex-items-center w-100">
              <h4 className="minthr-mb-2">{t('Raison')}</h4>
              <button
                className="minthr-sidebar-close minthr-ml-auto"
                type="button"
                onClick={() => {
                  dialog.close();
                }}
              >
                <img
                  className="minthr-icon minthr-icon-gray"
                  alt="Close"
                  src="/icons/close.svg"
                />
              </button>
            </div>
            <TextArea label={t('Raison')} name="raison" />

            <Button
              type="submit"
              className="minthr-btn minthr-btn-danger minthr-ml-auto minthr-mt-4"
              disabled={isSubmitting}
            >
              {t('btn.reject')}
            </Button>
          </Form>
        )}
      </Formik>,
    );
  };

  const handleAccept = async (): Promise<void> => {
    if (!mainResponse) return;
    setAccepting(true);

    const payload: Decision = {
      id: mainResponse.id,
      levelType: mainResponse.levelType,
      raison: '',
    };
    const response = await acceptedConge(payload);
    if (response.success) {
      setAccepting(false);
      closeSidebar(LeaveDetails);
      toast.success(t('Default.succesMessage'));
      if (params.refetch) {
        params.refetch();
      }
      setRefetch((prev) => !prev);
    } else {
      setAccepting(false);
      toast.error(t(response.message));
    }
  };

  const handleForceAccept = (id: number) => async (): Promise<void> => {
    dialog.success('Force.Accept', 'Force.Accept_desc').then(async (res) => {
      if (res) {
        const response = await forceAcceptConge(id);
        if (response.success) {
          closeSidebar(LeaveDetails);
          toast.success(t('Default.succesMessage'));
          if (params.refetch) {
            params.refetch();
          }
          setRefetch((prev) => !prev);
        } else {
          toast.error(response.message);
        }
      }
    });
  };

  const handleSubmitReject = async (raison: string): Promise<void> => {
    if (!mainResponse) return;

    const payload: Decision = {
      id: mainResponse.id,
      levelType: mainResponse.levelType,
      raison,
    };
    const response = await rejectConge(payload);
    if (response.success) {
      dialog.close();
      toast.success(t('Default.succesMessage'));
      setShowRejectReason(false);
      if (params?.refetch) {
        params.refetch();
      }
      setRefetch((prev) => !prev);
      closeSidebar(LeaveDetails);
    } else {
      toast.error(response.message);
    }
  };
  const handelOpenFile = (fileName: string, id: number): void => {
    const ext = fileName.split('.').pop() as string;
    openMedia(`/upload/VueFile/${id}`, ext, fileName);
  };
  if (!mainResponse) {
    return <Loader />;
  }

  return (
    <Sidebar
      size="m"
      type="primary"
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
    >
      <div className="minthr-flex minthr-flex-items-center minthr-pb-2 minthr-mb-3 minthr-border-b minthr-border-b-3">
        <div className="minthr-icon-bg minthr-icon-bg-l minthr-icon-bg-secondary minthr-icon-circle minthr-mr-3">
          <Image
            className="minthr-icon-m"
            src="/icons/stopwatch.svg"
            alt="Leave Info"
            width={24}
            height={24}
          />
        </div>
        <h3>{t('conge.info.title')}</h3>
        <button
          className="minthr-sidebar-close"
          type="button"
          onClick={() => closeSidebar(LeaveDetails)}
        >
          <Image src="/icons/close.svg" alt="Close" width={24} height={24} />
        </button>
      </div>

      {demandeUser && (
        <div className="minthr-pt-2 h-100 d-flex minthr-flex-column">
          <div className="row g-0 minthr-mb-2">
            <div className="col-sm-7">
              <div className="minthr-flex minthr-flex-items-center">
                <UserShortCut
                  userId={demandeUser?.id ?? ''}
                  userPath={demandeUser?.image ?? ''}
                  alt={demandeUser?.fullName}
                  className="minthr-avatar minthr-avatar-m minthr-mr-2"
                />

                <div className="w-100">
                  <h5 className="minthr-color-dark minthr-first-upper">
                    {demandeUser.fullName}
                  </h5>
                  <p className="text-s">{t(demandeUser.fonctionName)}</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="minthr-flex minthr-flex-items-center minthr-mb-3">
              <h5 className="minthr-color-primary">
                {t('Demande.Info.EditDemande.Title')}
              </h5>
              {mainResponse.statut !== 'Accepter' &&
                !(
                  approval?.some(
                    (x) => x.status === 'Accepter' && x.level !== 'Automatic',
                  ) ?? false
                ) && (
                  <>
                    {(hasPermission('Golbal.Edit.Demand') ||
                      (user.userId === mainResponse.fk_User &&
                        hasPermission('Persenal.Edit.Demand'))) && (
                      <button
                        type="button"
                        className="minthr-btn minthr-btn-s minthr-btn-icon-origin minthr-ml-auto minthr-p-0"
                        onClick={handleEdit}
                      >
                        <Image
                          src="/icons/edit.svg"
                          alt="Edit"
                          width={24}
                          height={24}
                        />
                      </button>
                    )}

                    {hasPermission('Conge.Delete.Demand') && (
                      <button
                        type="button"
                        className="minthr-btn minthr-btn-s minthr-btn-icon-origin minthr-icon-danger minthr-px-1"
                        onClick={handleDelete}
                      >
                        <Image
                          src="/icons/delete.svg"
                          alt="Delete"
                          width={24}
                          height={24}
                        />
                      </button>
                    )}
                    {mainResponse.statut == 'En_Cour' &&
                      (user.role === 'Proprietaire' ||
                        user.role === 'SupportMintHR') && (
                        <Tippy content={t('Force.Accept')}>
                          <button
                            type="button"
                            className="minthr-btn minthr-btn-s minthr-btn-icon-origin minthr-ml-1 minthr-p-0 minthr-opacity-50 minthr-hover-opacity-100"
                            onClick={handleForceAccept(Number(mainResponse.id))}
                          >
                            <img
                              className="minthr-icon-s"
                              src="/icons/accept.svg"
                              alt=""
                            ></img>
                          </button>
                        </Tippy>
                      )}
                  </>
                )}
            </div>

            <InfoRow
              label={
                mainResponse.durationUnit == 1
                  ? t('rigime.WeekType.TotaleHours')
                  : t('Demande.Info.EditDemande.Total_days')
              }
              value={`${String(mainResponse.durationValue).replace('.', ',') || 0}  ${mainResponse.solde ? '/' + mainResponse.solde : ''} ${mainResponse.durationUnit == 1 ? t('Time_Off_Hours') : t('Constant.day')}`}
              color="green"
            />
            <InfoRow
              label={t('Demande.Info.EditDemande.typeConge')}
              value={mainResponse.type || ''}
            />
            <StatusRow label={t('status')} status={mainResponse.statut || ''} />
            {/* <InfoRow label={t('period')} value={period || ''} /> */}

            <div className="row g-0 minthr-flex-items-center minthr-mb-2">
              <div className="col-sm-4">
                <span className="minthr-text-uppercase p">{t('period')}</span>
              </div>
              <div className="col-sm-8">
                <div className="minthr-color-dark h5">
                  {moment(mainResponse.startDate).format('DD MMM yyyy')}
                  {mainResponse.startTime == null ? (
                    <>
                      {mainResponse.morning && (
                        <img
                          className="minthr-icon-s minthr-ml-2 minthr-mr-1 minthr-icon-gray"
                          src="/icons/30-minutes.svg"
                          alt=""
                        />
                      )}

                      <img
                        className="minthr-icon-s minthr-mx-2 minthr-icon-gray"
                        src="/icons/arrow.svg"
                        alt=""
                      />
                      {mainResponse.afternoon && (
                        <img
                          className="minthr-icon-s minthr-ml-2 minthr-mr-1 minthr-icon-gray"
                          src="/icons/30-minutes.svg"
                          alt=""
                        />
                      )}
                      {moment(mainResponse.endDate).format('DD MMM yyyy')}
                    </>
                  ) : (
                    <>
                      {moment(mainResponse.startDate).format('DD MMM yyyy') !=
                      moment(mainResponse.endDate).format('DD MMM yyyy') ? (
                        <>
                          {' '}
                          <img
                            className="minthr-icon-s minthr-ml-2 minthr-mr-2 minthr-icon-gray"
                            src="/icons/arrow.svg"
                            alt=""
                          />
                          {moment(mainResponse.endDate).format('DD MMM yyyy')}
                          <br />
                        </>
                      ) : (
                        <br />
                      )}

                      {moment(mainResponse.startTime, 'HH:mm:ss')?.format(
                        'HH:mm',
                      )}
                      <img
                        className="minthr-icon-s minthr-ml-2 minthr-mr-2 minthr-icon-gray"
                        src="/icons/arrow.svg"
                        alt=""
                      />
                      {moment(mainResponse.endTime, 'HH:mm:ss').format('HH:mm')}
                    </>
                  )}
                </div>
              </div>
            </div>
            {mainResponse.projectName && (
              <InfoRow
                label={t('ProjectName')}
                value={mainResponse.projectName || ''}
              />
            )}
            <InfoRow
              label={t('description')}
              value={mainResponse.description || ''}
            />
            {mainResponse.filesNames &&
              mainResponse.filesNames.map((file, idx) => (
                <div key={idx}>
                  <div className="minthr-flex minthr-flex-items-center w-100 minthr-bg-light-gray minthr-text-decoration-none minthr-p-2 minthr-border-rounded minthr-mb-2 minthr-mt-3">
                    <img
                      className="minthr-icon-xl minthr-mr-2 minthr-filter-none"
                      src={`/icons/${getIconByExtension(file.name)}`}
                      alt=""
                    />
                    <h6 className="minthr-text-truncate m-0">{file.name}</h6>

                    {checkFileType(file.name) ? (
                      <button
                        onClick={() => handelOpenFile(file.name, file.id)}
                        className="minthr-p-0 minthr-border-none minthr-bg-transparent minthr-ml-auto minthr-mr-2 img-vue"
                      >
                        <img
                          className="minthr-icon-s minthr-icon-gray"
                          src="/icons/eye.svg"
                          alt="Download"
                        />
                      </button>
                    ) : (
                      <a
                        href={`/upload/DownloadFile/${file.id}`}
                        download={file.name}
                        className="minthr-p-0 minthr-border-none minthr-bg-transparent minthr-ml-auto minthr-mr-2 img-vue"
                      >
                        <img
                          className="minthr-icon-s minthr-icon-gray"
                          src="/icons/download.svg"
                          alt="Download"
                        />
                      </a>
                    )}
                  </div>
                </div>
              ))}

            <Timeline
              createBy={createBy as CreateBy}
              approval={approval}
              approvalId={mainResponse?.approvalId}
              refetch={fetchData}
            />
            <SameDepartmentRequests requests={departmentRequest as UserDep[]} />
          </div>
          {(mainResponse?.statut == 'En_Cour' ||
            (mainResponse?.statut == 'Accepter' &&
              hasPermission('Conge.Cancel.Demand'))) && (
            <div className="minthr-sidebar-footer minthr-box-shadow minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-py-4 minthr-mx-n5 minthr-px-4 minthr-mt-auto">
              <>
                {mainResponse.statut == 'En_Cour' &&
                  (hasPermission('Conge.Cancel.Demand') ||
                    user?.userId === mainResponse?.demandeUser?.id) && (
                    <Button
                      type="button"
                      className="minthr-btn minthr-btn-outline minthr-btn-warning minthr-px-6"
                      onClick={handleCancel}
                      loading={cancelling}
                    >
                      {t('btn.cancel')}
                    </Button>
                  )}
                {mainResponse.statut == 'En_Cour' &&
                  mainResponse.canApproved && (
                    <>
                      <Button
                        type="button"
                        className="minthr-btn minthr-btn-outline minthr-btn-danger minthr-px-6"
                        onClick={handleReject}
                      >
                        {t('btn.reject')}
                      </Button>
                      <Button
                        type="button"
                        className="minthr-btn minthr-btn-outline minthr-btn-primary minthr-px-6"
                        onClick={handleAccept}
                        loading={accepting}
                      >
                        {t('btn.accept')}
                      </Button>
                    </>
                  )}
                {mainResponse.statut == 'Accepter' &&
                  hasPermission('Conge.Cancel.Demand') && (
                    <Button
                      type="button"
                      className="minthr-btn minthr-btn-outline minthr-btn-warning minthr-px-6"
                      onClick={handleCancel}
                      loading={cancelling}
                    >
                      {t('btn.cancel')}
                    </Button>
                  )}
              </>
            </div>
          )}
        </div>
      )}
    </Sidebar>
  );
};

export default LeaveDetails;
